import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg shadow-sm hover:bg-accent-hover active:shadow-xs ' +
    'disabled:bg-accent/50',
  secondary:
    'bg-raised text-ink ring-hairline hover:bg-inset active:bg-inset ' +
    'shadow-xs disabled:text-subtle',
  ghost: 'text-muted hover:bg-inset hover:text-ink',
  subtle: 'bg-accent-soft text-accent-text hover:brightness-[0.97] dark:hover:brightness-125',
  danger: 'bg-danger text-white hover:brightness-95 shadow-sm',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 gap-1.5 rounded-md px-3 text-[13px]',
  md: 'h-11 gap-2 rounded-lg px-4 text-[14px]',
  lg: 'h-13 gap-2.5 rounded-xl px-6 text-[15px]',
};

/** A button's look, for an element that must be something else — a router link. */
export function buttonClasses(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return cn(
    'relative inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
    'transition-[background-color,color,box-shadow,transform] duration-200 ease-[var(--ease-out-quint)]',
    'active:scale-[0.985] disabled:pointer-events-none disabled:opacity-60',
    VARIANTS[variant],
    SIZES[size],
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(buttonClasses(variant, size), fullWidth && 'w-full', className)}
      {...rest}
    >
      {isLoading ? <Spinner className="size-4" /> : iconLeft}
      {children}
      {!isLoading && iconRight}
    </button>
  );
});
